import LanguageContent, {
  LegislationContent,
  SidebarContent,
} from "@/models/i18n/LanguageContent";
import LaraIcon from "@/assets/lara/lara-icon.svg";

import LodaDarkGreenIcon from "@/assets/loda/loda-dark-green-icon.svg";
import LodaLightGreenIcon from "@/assets/loda/loda-light-green-icon.svg";

import LodaDarkGreenLogo from "@/assets/loda/loda-dark-green-logo.svg";
import LodaLightGreenLogo from "@/assets/loda/loda-light-green-logo.svg";

const LODA_DEFAULT_SIDEBAR_CONTENT_EN: SidebarContent = {
  open: {
    logo: {
      dark: LodaDarkGreenLogo,
      light: LodaLightGreenLogo,
    },
  },
  collapsed: {
    logo: {
      dark: LodaDarkGreenIcon,
      light: LodaLightGreenIcon,
    },
  },
};

const DEFAULT_LEGISLATION_CONTENT_EN: LegislationContent = {
  listView: {
    heading: "Legislation",
  },
};

const LARA_DEFAULT_CHAT_CONTENT_EN = {
  disclaimer:
    "Lara can make mistakes. Please check important information with the official source.",
  inputPlaceholder: "Ask Lara...",
  icon: LaraIcon,
  emptyState: {
    greeting: {
      headline: "Let's make your legal research easier.",
      subtitle: "Your AI assistant for sources, workspaces, and clause checks.",
    },
    cards: {
      legalSources: {
        title: "Legal Sources",
        description:
          "Add Legal Information, Case Database, Arvid, and others for documented answers.",
      },
      ownSources: {
        title: "Own Sources",
        description:
          "Work in your cases. Add workspace, folders, or individual files.",
      },
      prompts: {
        title: "Get started with prompts",
        description:
          "Start with templates: clause check, find practice, compare versions.",
      },
    },
  },
  context: {
    noContextSelected: "No sources selected",
    noContextWarning:
      "No sources selected. Lara has access to information you provide, which may result in inaccurate answers.",
    addSource: "Add source",
    addWorkspace: "Add workspace",
    cannotAddSourceMessage:
      "You cannot add legal sources when you have selected workspace sources. Remove workspace sources first to add legal sources.",
    cannotAddWorkspaceMessage:
      "You cannot add workspace sources when you have selected legal sources. Remove legal sources first to add workspace sources.",
    selectWorkspaceTitle: "Select workspace folders & files",
    alreadyHasSourcesMessage:
      "You have already selected legal sources. To add workspace sources, you must first remove legal sources.",
    noWorkspaceFilesFound: "No workspace folders or files found",
    confirm: "Start conversation",
    filterCount: "{{count}} filters",
    dontShowAgain: "Don't show this dialog again",
    conversationStartsAutomatically:
      "When you have selected sources, the conversation will start automatically",
    continueAnyway: "Continue without sources",
    currentDataOnly:
      "Replies are based only on current data. Historic data is excluded.",
  },
  sourceSelection: {
    title: "Select legal sources",
    availableSources: "Available sources",
    configureFiltersFor: "Configure filters for:",
    selectSourceToConfigure: "Select a source on the left to configure filters",
    cancel: "Cancel",
    addSource: "Add source",
    sourceSelected: "1 source selected",
    cannotAddSourceWarning:
      "You have already selected workspace sources. To add legal sources, you must first remove workspace sources.",
    cannotAddSourceTooltip:
      "You cannot add legal sources when you have selected workspace sources. Remove workspace sources first.",
    selectSourcePrompt: "Please select a source.",
  },
};

const LODA_DEFAULT_CHAT_CONTENT_EN = {
  disclaimer:
    "Loda can make mistakes. Please check important information with the official source.",
  inputPlaceholder: "Ask Loda...",
  icon: LodaDarkGreenIcon,
  emptyState: {
    greeting: {
      headline: "Let's make your legal research easier.",
      subtitle: "Your AI assistant for sources, workspaces, and clause checks.",
    },
    cards: {
      legalSources: {
        title: "Legal Sources",
        description:
          "Add Legal Information, Case Database, Arvid, and others for documented answers.",
      },
      ownSources: {
        title: "Own Sources",
        description:
          "Work in your cases. Add workspace, folders, or individual files.",
      },
      prompts: {
        title: "Get started with prompts",
        description:
          "Start with templates: clause check, find practice, compare versions.",
      },
    },
  },
  context: {
    noContextSelected: "No sources selected",
    noContextWarning:
      "No sources selected. Loda has access to information you provide, which may result in inaccurate answers.",
    addSource: "Add source",
    addWorkspace: "Add workspace",
    cannotAddSourceMessage:
      "You cannot add legal sources when you have selected workspace sources. Remove workspace sources first to add legal sources.",
    cannotAddWorkspaceMessage:
      "You cannot add workspace sources when you have selected legal sources. Remove legal sources first to add workspace sources.",
    selectWorkspaceTitle: "Select workspace folders & files",
    alreadyHasSourcesMessage:
      "You have already selected legal sources. To add workspace sources, you must first remove legal sources.",
    noWorkspaceFilesFound: "No workspace folders or files found",
    confirm: "Start conversation",
    filterCount: "{{count}} filters",
    dontShowAgain: "Don't show this dialog again",
    conversationStartsAutomatically:
      "When you have selected sources, the conversation will start automatically",
    continueAnyway: "Continue without sources",
    currentDataOnly:
      "Replies are based only on current data. Historic data is excluded.",
  },
  sourceSelection: {
    title: "Select legal sources",
    availableSources: "Available sources",
    configureFiltersFor: "Configure filters for:",
    selectSourceToConfigure: "Select a source on the left to configure filters",
    cancel: "Cancel",
    addSource: "Add source",
    sourceSelected: "1 source selected",
    cannotAddSourceWarning:
      "You have already selected workspace sources. To add legal sources, you must first remove workspace sources.",
    cannotAddSourceTooltip:
      "You cannot add legal sources when you have selected workspace sources. Remove workspace sources first.",
    selectSourcePrompt: "Please select a source.",
  },
};

const LARA_DEFAULT_AUTH_CONTENT_EN = {
  logo: LaraIcon,
  text: "Log in to your account",
  button: {
    signIn: "Sign in",
    signOut: "Sign out",
  },
  theme: {
    toLight: "Switch to light mode",
    toDark: "Switch to dark mode",
  },
};

const LODA_DEFAULT_AUTH_CONTENT_EN = {
  logo: LodaDarkGreenLogo,
  text: "Log in to your account",
  button: {
    signIn: "Sign in",
    signOut: "Sign out",
  },
  theme: {
    toLight: "Switch to light mode",
    toDark: "Switch to dark mode",
  },
};

export const LARA_DEFAULT_CONTENT_EN: LanguageContent = {
  // sidebar: intentionally omitted - uses fallback in AppSidebarHeader
  legislation: DEFAULT_LEGISLATION_CONTENT_EN,
  chat: LARA_DEFAULT_CHAT_CONTENT_EN,
  auth: LARA_DEFAULT_AUTH_CONTENT_EN,
};

export const LODA_DEFAULT_CONTENT_EN: LanguageContent = {
  sidebar: LODA_DEFAULT_SIDEBAR_CONTENT_EN,
  legislation: DEFAULT_LEGISLATION_CONTENT_EN,
  chat: LODA_DEFAULT_CHAT_CONTENT_EN,
  auth: LODA_DEFAULT_AUTH_CONTENT_EN,
};
